import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpLeft, ArrowDownRight, MapPin, Clock, Sprout, FlaskConical, Users } from "lucide-react";
import Footer from "@/components/athena/Footer";

const VALUES = [
  {
    icon: FlaskConical,
    title: "Rigour without theatre",
    body: "We build products worth defending in a lab and in front of a customer. Craft matters more than volume here.",
  },
  {
    icon: Sprout,
    title: "Nature is a co-author",
    body: "The best formulations start with molecules that already earned their place. We amplify, we don't invent detours.",
  },
  {
    icon: Users,
    title: "Small team, wide surface",
    body: "You will touch discovery, formulation, analytics and go-to-market. Titles are fluid; ownership is not.",
  },
];

const OPEN_ROLES = [
  {
    id: "formulation-scientist",
    title: "Formulation Scientist",
    dept: "R&D",
    location: "Mumbai, India · On-site",
    type: "Full-time",
    tags: ["Bioavailability", "Lipid encapsulation", "Botanicals"],
    summary:
      "Lead formulation development from raw actives to prototype. Own bioavailability engineering, encapsulation strategy and dose optimisation.",
  },
  {
    id: "bioprocess-engineer",
    title: "Bioprocess Engineer",
    dept: "Biotechnology",
    location: "Mumbai, India · On-site",
    type: "Full-time",
    tags: ["Fermentation", "Scale-up", "USP/DSP"],
    summary:
      "Design and scale fermentation and downstream processes for microbial-derived actives. Bench-to-100 L pilot experience preferred.",
  },
  {
    id: "clinical-affairs-associate",
    title: "Clinical Affairs Associate",
    dept: "Life Sciences",
    location: "Mumbai, India · Hybrid",
    type: "Full-time",
    tags: ["IRB", "Study design", "Regulatory"],
    summary:
      "Support the design, execution and reporting of IRB-approved clinical studies. Draft dossiers and regulatory correspondence.",
  },
  {
    id: "qa-analyst",
    title: "QA / Analytical Chemist",
    dept: "Quality",
    location: "Mumbai, India · On-site",
    type: "Full-time",
    tags: ["HPLC", "Stability", "Method development"],
    summary:
      "Own analytical workflows for identity, potency and stability testing. Develop and validate methods against internal specifications.",
  },
  {
    id: "product-manager",
    title: "Product Manager — Nutraceuticals",
    dept: "Product",
    location: "Mumbai, India · Hybrid",
    type: "Full-time",
    tags: ["B2B partnerships", "Roadmap", "Go-to-market"],
    summary:
      "Own the roadmap for select formulations. Translate scientific dossiers into commercial narratives and partner-ready propositions.",
  },
  {
    id: "marketing-executive",
    title: "Marketing Executive",
    dept: "Marketing",
    location: "Mumbai, India · Hybrid",
    type: "Full-time",
    tags: ["B2B content", "Digital campaigns", "Brand"],
    summary:
      "Own end-to-end marketing execution — from digital campaigns and content to trade collateral and partner outreach. Bridge the science team and our external audience with clarity and craft.",
  },
];

export default function Careers() {
  useEffect(() => {
    document.title = "Careers — Athena Nutrascience";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-[#F8F7F4] min-h-screen">
      {/* Top bar */}
      <header className="border-b border-[#D5D1C8] bg-[#F8F7F4]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="careers-nav-home"
            className="flex items-center gap-3 group"
          >
            <span className="w-8 h-8 rounded-full border border-[#1A382A] flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[#1A382A] group-hover:bg-[#B89B64] transition-colors" />
            </span>
            <span className="font-display text-2xl tracking-tight text-[#0A1713] leading-none">
              Athena<span className="text-[#B89B64]">.</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="careers-back-link"
            className="flex items-center gap-2 label-mono hover:text-[#1A382A] transition-colors"
          >
            <ArrowUpLeft size={14} strokeWidth={1.5} /> BACK TO HOME
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <span className="label-mono">// CAREERS AT ATHENA</span>
            <h1
              data-testid="careers-title"
              className="font-display text-5xl sm:text-6xl md:text-7xl mt-6 leading-[0.98] tracking-tight text-[#0A1713]"
            >
              Build the
              <br />
              <em className="italic text-[#1A382A]">next molecule</em>
              <br />
              with us.
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-[#3D4F46] leading-relaxed">
              We are a small, deliberate team of scientists, engineers and
              operators building nutraceuticals, biologics and translational
              research programmes. If you take craft seriously and want your
              work to actually land in the world — read on.
            </p>
            <a
              href="#open-roles"
              data-testid="careers-cta"
              className="btn-primary mt-10 inline-flex"
            >
              See open roles <ArrowDownRight size={16} strokeWidth={1.5} />
            </a>
          </div>

          <aside className="lg:col-span-4 border border-[#D5D1C8] bg-[#EAE7E0] p-8">
            <div className="label-mono">// LIFE AT ATHENA</div>
            <ul className="mt-6 space-y-4 text-sm text-[#3D4F46] leading-relaxed">
              <li className="flex gap-3">
                <span className="w-1 h-1 rounded-full bg-[#B89B64] mt-2 shrink-0" />
                <span><strong>Ownership from day one</strong> — no diluted scope</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 rounded-full bg-[#B89B64] mt-2 shrink-0" />
                <span><strong>Learning stipend</strong> for books, courses & conferences</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 rounded-full bg-[#B89B64] mt-2 shrink-0" />
                <span><strong>Publication-friendly</strong> — we co-author, we don't gate</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 rounded-full bg-[#B89B64] mt-2 shrink-0" />
                <span><strong>Health & wellness cover</strong> for you and your family</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 rounded-full bg-[#B89B64] mt-2 shrink-0" />
                <span><strong>ESOP participation</strong> for every full-time member</span>
              </li>
            </ul>
          </aside>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-[#D5D1C8] pt-14 pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <span className="label-mono">// HOW WE WORK</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.04] tracking-tight text-[#0A1713]">
            Three ideas
            <br />
            we hire for.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-12">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="bg-[#F8F7F4] border border-[#D5D1C8] p-8 md:p-10 flex flex-col min-h-[240px]"
                >
                  <Icon size={28} strokeWidth={1.2} className="text-[#1A382A]" />
                  <h3 className="font-display text-2xl md:text-3xl mt-8 text-[#0A1713] leading-tight">
                    {v.title}
                  </h3>
                  <p className="mt-4 text-sm md:text-base text-[#3D4F46] leading-relaxed">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section
        id="open-roles"
        className="border-t border-[#D5D1C8] pt-14 pb-16 scroll-mt-32"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <span className="label-mono">// OPEN ROLES</span>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.04] tracking-tight text-[#0A1713]">
                Where we&apos;re
                <br />
                looking now.
              </h2>
            </div>
            <p className="max-w-md text-[#3D4F46] leading-relaxed">
              Six openings across R&amp;D, biotech, quality, product and
              marketing. Don&apos;t see your role? See the note at the bottom.
            </p>
          </div>

          <ul className="border-t border-[#0A1713]/15">
            {OPEN_ROLES.map((r) => (
              <li
                key={r.id}
                data-testid={`careers-role-${r.id}`}
                className="grid grid-cols-12 gap-4 md:gap-6 items-start py-8 md:py-10 border-b border-[#0A1713]/15 group"
              >
                <div className="col-span-12 md:col-span-4">
                  <div className="label-mono text-[#1A382A]">/ {r.dept}</div>
                  <div className="font-display text-2xl md:text-3xl mt-2 text-[#0A1713] leading-tight">
                    {r.title}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-xs font-mono uppercase tracking-wider text-[#3D4F46]">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} strokeWidth={1.5} className="text-[#B89B64]" />
                      {r.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={12} strokeWidth={1.5} className="text-[#B89B64]" />
                      {r.type}
                    </span>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 text-[#3D4F46] leading-relaxed text-sm md:text-base">
                  {r.summary}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#1A382A] border border-[#D5D1C8] bg-[#EAE7E0] px-2 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end md:pt-3">
                  <a
                    href={`mailto:connect@athenanutrascience.com?subject=Application%20—%20${encodeURIComponent(r.title)}`}
                    data-testid={`careers-apply-${r.id}`}
                    className="btn-secondary text-xs !py-3 !px-4"
                  >
                    Apply <ArrowDownRight size={14} strokeWidth={1.5} />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Open application */}
      <section className="bg-[#0A1713] text-[#F8F7F4] py-16 md:py-20">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <span className="label-mono">// DON&apos;T SEE YOUR ROLE</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight">
            Send us a note anyway.
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[#A2B3AB] leading-relaxed">
            If you are excellent at something adjacent to what we do, we
            probably want to hear from you. Tell us what you build best and
            what excites you about Athena.
          </p>
          <a
            href="mailto:connect@athenanutrascience.com?subject=Open%20Application"
            data-testid="careers-open-application"
            className="btn-primary btn-on-dark mt-10 inline-flex"
          >
            Write to us <ArrowDownRight size={16} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
